<script lang="ts">
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		get_device_component_index,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		isDimmerWithTransitionMinLimitations,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { deviceHasSettingTitle } from 'src/helpers/settings/device_has_setting_title';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { Container, InputV2, SliderV2, Text } from '@Shelly/shelly-design-system';
	import SettingGuide from '../../../SettingGuide.svelte';

	export let device: Device;

	const id = real_id(device.id);
	const componentKey = get_gen_2_component_channel_structure(device);
	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;
	const componentIndex = get_device_component_index(device);
	const showFade = deviceHasSettingTitle(device, 'button_fade_rate');
	const showMinBrightness = deviceHasSettingTitle(device, 'min_brightness_on_toggle');
	const showTransition = deviceHasSettingTitle(device, 'transition_duration');

	// RGBW/Light docs: transition_duration is seconds for 0%→100%. Some US dimmers reject 0.
	const transitionMin = isDimmerWithTransitionMinLimitations(device.type) ? 1 : 0;
	const transitionMax = 5;
	const brightnessMin = 0;
	const brightnessMax = 100;
	// button_fade_rate range [1,5]; 5 is fastest (API docs).
	const fadeRateMin = 1;
	const fadeRateMax = 5;

	const transitionMarkers = [...Array(transitionMax - transitionMin + 1)].map((_, index) => {
		const value = transitionMin + index;
		return { value, showLabel: true, displayValue: `${value}s` };
	});
	const fadeRateMarkers = [...Array(fadeRateMax - fadeRateMin + 1)].map((_, index) => {
		const value = fadeRateMin + index;
		return { value, showLabel: true, displayValue: `${value}x` };
	});

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	$: componentSettings = settings?.[componentKey];

	let fadeRate = 3;
	let cacheFade = fadeRate;
	let transitionValue = transitionMin;
	let cacheTransition = transitionValue;
	let minBrightness = '0';
	let cacheMinBrightness = minBrightness;
	let hydrated = false;
	let loading = false;

	function clampInt(value: unknown, min: number, max: number, fallback: number): number {
		const num = typeof value === 'number' ? value : Number(value);
		if (Number.isNaN(num)) return fallback;
		return Math.min(max, Math.max(min, Math.round(num)));
	}

	function formatBrightnessField(value: unknown): string {
		if (value === undefined || value === null || Number.isNaN(Number(value))) return '0';
		return String(value);
	}

	/** Sync from device settings when they arrive or update. Snap transition to whole seconds. */
	function hydrateFromSettings(nextSettings: Record<string, any> | undefined) {
		if (!nextSettings) return;

		if (showFade) {
			const nextFade = clampInt(nextSettings.button_fade_rate, fadeRateMin, fadeRateMax, 3);
			if (!hydrated || fadeRate === cacheFade) {
				fadeRate = nextFade;
				cacheFade = nextFade;
			}
		}

		if (showTransition) {
			const nextTransition = clampInt(
				nextSettings.transition_duration,
				transitionMin,
				transitionMax,
				transitionMin,
			);
			if (!hydrated || transitionValue === cacheTransition) {
				transitionValue = nextTransition;
				cacheTransition = nextTransition;
			}
		}

		if (showMinBrightness) {
			const nextBrightness = formatBrightnessField(nextSettings.min_brightness_on_toggle);
			if (!hydrated || minBrightness === cacheMinBrightness) {
				minBrightness = nextBrightness;
				cacheMinBrightness = nextBrightness;
			}
		}

		hydrated = true;
	}

	$: hydrateFromSettings(componentSettings);

	$: brightnessNum = minBrightness === '' ? NaN : Number(minBrightness);

	$: fadeDirty = showFade && fadeRate !== cacheFade;
	$: transitionDirty = showTransition && transitionValue !== cacheTransition;
	$: brightnessDirty = showMinBrightness && minBrightness !== cacheMinBrightness;
	$: isDirty = fadeDirty || transitionDirty || brightnessDirty;

	const rangeMessage = (min: number, max: number) =>
		$format('min_max_error_description', {
			values: { min, max },
			default: 'Value must be between {min} and {max}.',
		});

	$: brightnessData =
		minBrightness === '' || Number.isNaN(brightnessNum)
			? brightnessDirty
				? { error: rangeMessage(brightnessMin, brightnessMax), info: '' }
				: { error: '', info: rangeMessage(brightnessMin, brightnessMax) }
			: getErrorOrInfoMessageForMinAndMax(brightnessNum, brightnessMin, brightnessMax);

	$: hasError = showMinBrightness && !!brightnessData.error;

	$: transitionA11y = {
		ariaLabel: $format('settings_label_fade_duration', { default: 'Fade duration' }),
		ariaLabelValue: $format('settings_a11y_fade_duration_value', {
			values: { value: transitionValue },
			default: 'Fade duration is {value} seconds',
		}),
	};

	$: fadeRateA11y = {
		ariaLabel: $format('settings_label_hold_to_dim_speed', { default: 'Hold-to-dim speed' }),
		ariaLabelValue: $format('settings_a11y_hold_to_dim_speed_value', {
			values: { value: fadeRate },
			default: 'Hold-to-dim speed is {value}x',
		}),
	};

	const save = async () => {
		if (!isDirty || hasError || loading) return;

		const config: Record<string, number> = {};
		if (fadeDirty) {
			config.button_fade_rate = fadeRate;
		}
		if (transitionDirty) {
			config.transition_duration = transitionValue;
		}
		if (brightnessDirty) {
			if (Number.isNaN(brightnessNum)) return;
			config.min_brightness_on_toggle = brightnessNum;
		}
		if (Object.keys(config).length === 0) return;

		loading = true;
		const res: SettingRequest = await gen2Request(device, method, {
			id: componentIndex,
			config,
		});
		if (res.isok) {
			const next = devices_settings.getByRealId(id) ?? settings;
			if (next?.[componentKey]) {
				Object.assign(next[componentKey], config);
				devices_settings.update_devices_settings(id, next);
			}
			if (config.button_fade_rate !== undefined) cacheFade = fadeRate;
			if (config.transition_duration !== undefined) cacheTransition = transitionValue;
			if (config.min_brightness_on_toggle !== undefined) cacheMinBrightness = minBrightness;
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: save,
				loading,
				disabled: loading || !isDirty || hasError,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<!-- Cards layout (SETTINGS.md): each distinct setting in its own Container. -->
<div class="dimming-behavior">
	<SettingGuide
		text={$format('settings_guide_dimming', {
			default:
				'How quickly brightness changes, how fast hold-to-dim runs, and the lowest brightness used when turning the light on.',
		})}
	/>

	{#if showTransition}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_label_fade_duration', { default: 'Fade duration' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('alert_heading_default_transition', {
					default: 'Transition time (in seconds) when transition is not explicitly set.',
				})}
			</Text>
			<SliderV2
				id="slider-dimming-transition"
				a11yProps={transitionA11y}
				bind:value={transitionValue}
				on:value_change={(event) => {
					transitionValue = event.detail.value;
				}}
				min={transitionMin}
				max={transitionMax}
				step={1}
				type="incremented"
				unit="s"
				lineMarkers={transitionMarkers}
				withOverlay={false}
			/>
		</Container>
	{/if}

	{#if showFade}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_label_hold_to_dim_speed', { default: 'Hold-to-dim speed' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('alert_heading_button_fade_rate', {
					default:
						'Controls how quickly the output brightness changes while the button is held down for dimming.',
				})}
			</Text>
			<SliderV2
				id="slider-dimming-fade-rate"
				a11yProps={fadeRateA11y}
				bind:value={fadeRate}
				on:value_change={(event) => {
					fadeRate = event.detail.value;
				}}
				min={fadeRateMin}
				max={fadeRateMax}
				step={1}
				type="incremented"
				unit=""
				lineMarkers={fadeRateMarkers}
				withOverlay={false}
			/>
		</Container>
	{/if}

	{#if showMinBrightness}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_label_minimum_on_brightness', { default: 'Minimum on brightness' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('alert_heading_min_brightness_on_toggle', {
					default:
						"Brightness level (in percent) applied when there is a toggle and current brightness is lower than 'Min brightness on toggle'.",
				})}
			</Text>
			<InputV2
				size="m"
				width="full"
				type="number"
				bind:value={minBrightness}
				min={brightnessMin}
				max={brightnessMax}
				step={1}
				error={brightnessData.error}
				info={brightnessData.info}
				label={$format('collapse_percent_state', { default: 'Percent' })}
				id="input-dimming-min-brightness"
				aria-label={$format('settings_label_minimum_on_brightness', {
					default: 'Minimum on brightness',
				})}
			/>
		</Container>
	{/if}
</div>

<style>
	.dimming-behavior {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
